if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
            this.productElement.classList.forEach((classApplied) => {
              if (classApplied.startsWith('color-') || classApplied === 'gradient')
                this.modalContent.classList.add(classApplied);
            });
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();
            this.updateRetailPricesForQuickAdd();
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-selects');
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      updateRetailPricesForQuickAdd() {
        // Find all product forms in the modal
        const productForms = this.modalContent.querySelectorAll('form[id^="product-form-"], form[action*="/cart/add"]');
        
        productForms.forEach(form => {
          // Get product and variant information
          const variantInput = form.querySelector('input[name="id"]');
          if (!variantInput) return;
          
          const variantId = variantInput.value.replace(/\D/g, '');
          const productWrapper = form.closest('[data-product-id]');
          const productId = productWrapper?.dataset.productId;
          
          // Get current price from the modal's price element
          let currentPrice = null;
          const priceElement = this.modalContent.querySelector('.price .money');
          if (priceElement) {
            const priceText = priceElement.textContent.replace(/[$,]/g, '');
            const price = parseFloat(priceText);
            if (!isNaN(price)) {
              currentPrice = Math.round(price * 100); // Convert to cents like PDP does
            }
          }
          
          if (variantId && productId) {
            // For modal, use the original fetchRetailPrice since it's a single product context
            // and the modal content is isolated
            const sectionId = form.getAttribute('id')?.replace('product-form-', '').replace(/^quickadd-/, '');
            if (window.fetchRetailPrice) {
              window.fetchRetailPrice(productId, variantId, { sectionId, currentPrice });
            }
          }
        });
      }
    }
  );
}

// Product-specific retail price fetching function for PLP
function fetchRetailPriceForProduct(productId, variantId, form, currentPrice) {
  
  const baseURL = window.apiUrl;
  return fetch(`${baseURL}/storefront/products/${productId}/retail-prices`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        const variant = data.variants.find(v => {
          const rawId = v.id.toString().split('/').pop();
          return rawId === variantId;
        });

        if (!variant || isNaN(parseFloat(variant.price))) {
          // Remove retail price property from this specific form
          const input = form.querySelector('#RetailPriceHidden');
          if (input) input.remove();
          return;
        }

        const parsedRetailPrice = parseFloat(variant.price);

        // Add retail price property to this specific form only
        let input = form.querySelector('#RetailPriceHidden');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[_Retail Price]';
          input.id = 'RetailPriceHidden';
          form.appendChild(input);
        }
        input.value = parsedRetailPrice.toString();

        // Get variant price for comparison
        let variantPrice = null;
        if (currentPrice !== undefined && currentPrice !== null) {
          variantPrice = parseFloat(currentPrice) / 100;
        }

        // Note: Only add the property, don't display retail price on PLP
      })
      .catch(error => {
        console.error('Error fetching retail price:', error);
        const input = form?.querySelector('#RetailPriceHidden');
        if (input) input.remove();
      });
}

// Handle retail prices for quick-add forms in product cards
document.addEventListener('DOMContentLoaded', () => {
  // Find all quick-add forms in product cards
  const quickAddForms = document.querySelectorAll('.quick-add form[id^="quick-add-"]');
  
  quickAddForms.forEach(form => {
    // Get product and variant information
    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput) return;
    
    const variantId = variantInput.value.replace(/\D/g, '');
    
    // Get product ID from product-form data attribute
    const productForm = form.closest('product-form');
    const productId = productForm?.dataset.productId;
    
    // Get current price from the card's price element
    let currentPrice = null;
    const productCard = form.closest('.card-wrapper');
    if (productCard) {
      const priceElement = productCard.querySelector('.price .money');
      if (priceElement) {
        const priceText = priceElement.textContent.replace(/[$,]/g, '');
        const price = parseFloat(priceText);
        if (!isNaN(price)) {
          currentPrice = Math.round(price * 100); // Convert to cents like PDP does
        }
      }
    }
    
    if (variantId && productId) {
      // Call our product-specific retail price function
      fetchRetailPriceForProduct(productId, variantId, form, currentPrice);
    }
  });
});
